/**
 * GroupMembersDialog — manage members of a single worker group (P2.4).
 *
 * Two-pane dialog: current members on top with a remove (✕) per row,
 * and an Autocomplete picker below that lists every worker NOT
 * already in the group. Selecting workers + clicking "Add Selected"
 * dispatches a POST that the backend silently dedupes, so we don't
 * have to compute the diff client-side.
 *
 * On every change we re-fetch the member list so member_count and
 * row state stay in sync. Parent (WorkerGroups.tsx) gets a callback
 * after each change so the table can update its count column without
 * waiting for the dialog to close.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, PersonAddOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, systemApi, type Schema } from '../../../api';
import { workersApi, type WorkerResponse } from '../../../api/organization';
import { translateApiError } from '../../../i18n/translateApiError';

type WorkerGroup = Schema<'WorkerGroupResponse'>;
type WorkerGroupMember = Schema<'WorkerGroupMemberResponse'>;

interface Props {
  group: WorkerGroup | null;
  onClose: () => void;
  /**
   * Notify parent so the WorkerGroups table can refresh its
   * member_count column after each attach/detach. The parent owns
   * the canonical group list, so we don't pass a count back —
   * just signal a change happened.
   */
  onChanged?: () => void;
}

const GroupMembersDialog: React.FC<Props> = ({ group, onClose, onChanged }) => {
  const { t } = useTranslation();
  const open = !!group;

  // Server state
  const [members, setMembers] = useState<WorkerGroupMember[]>([]);
  const [allWorkers, setAllWorkers] = useState<WorkerResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add-form state
  const [picked, setPicked] = useState<WorkerResponse[]>([]);
  const [adding, setAdding] = useState(false);

  // Per-row remove tracking — multiple rows can be in flight
  const [removing, setRemoving] = useState<Set<string>>(new Set());

  const reload = useCallback(async () => {
    if (!group) return;
    setLoading(true);
    setError(null);
    try {
      const [m, w] = await Promise.all([
        systemApi.workerGroupsApi.listMembers(group.id) as Promise<WorkerGroupMember[]>,
        workersApi.list('active'),
      ]);
      setMembers(m);
      setAllWorkers(w);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('org:workerGroups.membersDialog.loadError'));
    } finally {
      setLoading(false);
    }
  }, [group, t]);

  useEffect(() => {
    if (open) {
      void reload();
    } else {
      // Reset on close so the next open starts clean
      setMembers([]);
      setAllWorkers([]);
      setPicked([]);
      setError(null);
    }
  }, [open, reload]);

  // Workers eligible to be added (active + not already a member).
  // Computed client-side; the backend dedupes anyway.
  const memberIds = useMemo(
    () => new Set(members.map((m) => m.worker_id)),
    [members],
  );
  const candidates = useMemo(
    () => allWorkers.filter((w) => !memberIds.has(w.id)),
    [allWorkers, memberIds],
  );

  const handleAdd = async () => {
    if (!group || picked.length === 0) return;
    setAdding(true);
    setError(null);
    try {
      await systemApi.workerGroupsApi.addMembers(
        group.id,
        picked.map((w) => w.id),
      );
      setPicked([]);
      await reload();
      onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('org:workerGroups.membersDialog.addError'));
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (workerId: string) => {
    if (!group) return;
    setRemoving((prev) => new Set(prev).add(workerId));
    setError(null);
    try {
      await systemApi.workerGroupsApi.removeMember(group.id, workerId);
      // Optimistic-ish: remove from local state without waiting for
      // a full reload, but still call reload so candidates list
      // updates with the now-eligible worker.
      setMembers((prev) => prev.filter((m) => m.worker_id !== workerId));
      onChanged?.();
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('org:workerGroups.membersDialog.removeError'));
    } finally {
      setRemoving((prev) => {
        const next = new Set(prev);
        next.delete(workerId);
        return next;
      });
    }
  };

  const workerLabel = (w: WorkerResponse) => {
    const name = `${w.first_name} ${w.last_name}`.trim();
    return `${name}${w.email ? ` (${w.email})` : ''}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.0625rem' }}>
            {group?.name ?? ''} {t('org:workerGroups.membersDialog.titleSuffix')}
          </Typography>
          {group?.description && (
            <Typography variant="caption" color="text.secondary">
              {group.description}
            </Typography>
          )}
        </Box>
        <IconButton size="small" onClick={onClose} aria-label={t('org:workerGroups.membersDialog.closeAria')}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Add panel */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{
              fontWeight: 600,
              fontSize: '0.6875rem',
              color: 'text.secondary',
              letterSpacing: '0.08em',
              display: 'block',
              mb: 1,
            }}
          >
            {t('org:workerGroups.membersDialog.addPanelTitle')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="flex-start">
            <Autocomplete
              multiple
              size="small"
              fullWidth
              options={candidates}
              value={picked}
              onChange={(_, v) => setPicked(v)}
              getOptionLabel={workerLabel}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={workerLabel(option)}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    candidates.length === 0
                      ? t('org:workerGroups.membersDialog.allMembersPlaceholder')
                      : t('org:workerGroups.membersDialog.searchPlaceholder')
                  }
                />
              )}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              size="small"
              startIcon={
                adding ? <CircularProgress size={14} color="inherit" /> : <PersonAddOutlined />
              }
              disabled={adding || picked.length === 0}
              onClick={handleAdd}
              sx={{ textTransform: 'none', height: 40, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
            >
              {t('org:workerGroups.membersDialog.addSelectedButton')}
            </Button>
          </Stack>
        </Box>

        {/* Member table */}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 600,
            fontSize: '0.6875rem',
            color: 'text.secondary',
            letterSpacing: '0.08em',
            display: 'block',
            mb: 1,
          }}
        >
          {t('org:workerGroups.membersDialog.currentMembersTitle', { count: members.length })}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : members.length === 0 ? (
          <Box
            sx={{
              border: '1px dashed rgba(0,0,0,0.16)',
              borderRadius: 1.5,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('org:workerGroups.membersDialog.emptyState')}
            </Typography>
          </Box>
        ) : (
          <TableContainer
            sx={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 1.5 }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('org:workerGroups.membersDialog.table.name')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('org:workerGroups.membersDialog.table.code')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('org:workerGroups.membersDialog.table.department')}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('org:workerGroups.membersDialog.table.position')}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, width: 56 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((m) => (
                  <TableRow key={m.worker_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {`${m.first_name} ${m.last_name}`.trim() || '—'}
                      </Typography>
                      {m.email && (
                        <Typography variant="caption" color="text.secondary">
                          {m.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{m.worker_code}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {m.department_name ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {m.position_name ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        aria-label={t('org:workerGroups.membersDialog.removeAria', { name: m.first_name })}
                        disabled={removing.has(m.worker_id)}
                        onClick={() => handleRemove(m.worker_id)}
                        sx={{ '&:hover': { color: 'error.main' } }}
                      >
                        {removing.has(m.worker_id) ? (
                          <CircularProgress size={14} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 16 }} />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          {t('org:workerGroups.membersDialog.doneButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GroupMembersDialog;
